/**
 * RiskCard — three pre-issue checks per `11-new-invoice-screen-spec.md`
 * §10:
 *
 *   1. Customer GSTIN verified · place-of-supply matches.
 *   2. Discount within threshold (15% per system-decisions §12).
 *   3. Credit usage within limit.
 *
 * Each check has three states: `ok` (green ●), `warn` (yellow ◐),
 * `block` (red ✕). The header summarises the worst state — `block`
 * disables the issue button at the orchestrator level; the SPA
 * doesn't enforce that here, just renders.
 *
 * Credit usage requires receipts (V1.x) to compute outstanding;
 * V1.0.x shows `'—'` with a hint when receipts haven't shipped, and
 * blocks the check from going `block` (it stays `ok` to avoid false
 * negatives that would prevent invoicing entirely).
 *
 * Pure presentation. Inputs come from the composer's computed state.
 */
import { Check, AlertTriangle, X, Info } from "lucide-react";
import { Card, CardBody } from "@flint/ui";
import { formatRupees } from "./lib/format-money.ts";
import { SectionHeading } from "./section-heading.tsx";

export type RiskState = "ok" | "warn" | "block" | "pending";

export interface RiskCardProps {
  /** Customer GSTIN status. `ok` = present + state-of-supply matches. */
  gstinState: RiskState;
  gstinDetail: string;

  /** Discount check. `block` when any line discount exceeds 15%. */
  discountState: RiskState;
  discountDetail: string;

  /** Credit usage. `pending` when receipts vertical hasn't shipped. */
  creditState: RiskState;
  creditDetail: string;

  /**
   * Optional alert that overrides the "ready to issue" banner with a
   * specific message — shown when the parent has a higher-priority
   * blocker (e.g. company GSTIN not configured).
   */
  blockerOverride?: { title: string; description: string };
}

export function RiskCard({
  gstinState,
  gstinDetail,
  discountState,
  discountDetail,
  creditState,
  creditDetail,
  blockerOverride,
}: RiskCardProps) {
  const worst: RiskState = blockerOverride
    ? "block"
    : worstState([gstinState, discountState, creditState]);
  const headline = blockerOverride
    ? { title: blockerOverride.title, body: blockerOverride.description }
    : worst === "block"
      ? {
          title: "Fix before issue",
          body: "One or more checks block issue. Resolve the items below.",
        }
      : worst === "warn"
        ? {
            title: "Needs founder approval",
            body: "A check needs Founder approval before this invoice can be issued.",
          }
        : worst === "pending"
          ? {
              title: "Partial check",
              body: "Credit-limit math arrives with the receipts vertical. Other checks pass.",
            }
          : {
              title: "Ready to issue",
              body: "All three checks pass. No founder approval required.",
            };

  const panel = panelStyles(worst);
  const { Icon: HeaderIcon } = stateMeta(worst);

  return (
    <Card>
      <SectionHeading step={5} title="Risk & approval" description="Flint checks before issue." />
      <CardBody className="flex flex-col gap-3.5">
        {/* Single tinted panel — color and left border key off the
         *  worst-state. Bulleted list mirrors the prototype's S12
         *  `.risk` block (one panel, three lines, no per-row pills). */}
        <div
          className={`flex items-start gap-3 rounded-md border border-l-4 px-3.5 py-3 ${panel.wrap}`}
        >
          <span aria-hidden className={`mt-0.5 shrink-0 ${panel.icon}`}>
            <HeaderIcon size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <strong className={`block text-sm font-semibold ${panel.title}`}>
              {headline.title}
            </strong>
            <p className={`mt-0.5 text-sm ${panel.body}`}>{headline.body}</p>
            <ul className="mt-2 flex flex-col gap-1.5 text-sm">
              <Bullet state={gstinState}>{gstinDetail}</Bullet>
              <Bullet state={discountState}>{discountDetail}</Bullet>
              <Bullet state={creditState}>{creditDetail}</Bullet>
            </ul>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function Bullet({ state, children }: { state: RiskState; children: React.ReactNode }) {
  const { Icon, dot, srLabel } = stateMeta(state);
  return (
    <li className="flex items-start gap-2">
      <span
        aria-hidden
        title={srLabel}
        className={`mt-0.5 inline-grid h-4 w-4 place-items-center rounded-full shrink-0 ${dot}`}
      >
        <Icon size={10} />
      </span>
      <span className="text-ink-2 flex-1">{children}</span>
    </li>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function worstState(states: RiskState[]): RiskState {
  if (states.includes("block")) return "block";
  if (states.includes("warn")) return "warn";
  if (states.every((s) => s === "ok")) return "ok";
  return "pending";
}

/** Per-state icon + the bullet "dot" classes. Keep these as fixed
 *  Tailwind class strings (no `bg-${tone}-50` interpolation) so the
 *  oxlint+JIT toolchain picks them up at build time. */
function stateMeta(state: RiskState): {
  Icon: typeof Check;
  /** Classes for the small icon "dot" used in bullets. */
  dot: string;
  srLabel: string;
} {
  switch (state) {
    case "ok":
      return {
        Icon: Check,
        dot: "bg-emerald-100 text-emerald-700",
        srLabel: "Pass",
      };
    case "warn":
      return {
        Icon: AlertTriangle,
        dot: "bg-amber-100 text-amber-700",
        srLabel: "Needs approval",
      };
    case "block":
      return {
        Icon: X,
        dot: "bg-rose-100 text-rose-700",
        srLabel: "Blocked",
      };
    case "pending":
      return {
        Icon: Info,
        dot: "bg-sky-100 text-sky-700",
        srLabel: "Pending",
      };
  }
}

/** Panel container styles keyed off the worst risk state — mirrors
 *  the prototype's `.risk` / `.risk.warn` / `.risk.bad` variants. */
function panelStyles(state: RiskState): {
  wrap: string;
  icon: string;
  title: string;
  body: string;
} {
  switch (state) {
    case "ok":
      return {
        wrap: "border-emerald-100 border-l-emerald-500 bg-emerald-50",
        icon: "text-emerald-600",
        title: "text-emerald-900",
        body: "text-emerald-800",
      };
    case "warn":
      return {
        wrap: "border-amber-100 border-l-amber-500 bg-amber-50",
        icon: "text-amber-600",
        title: "text-amber-900",
        body: "text-amber-800",
      };
    case "block":
      return {
        wrap: "border-rose-100 border-l-rose-500 bg-rose-50",
        icon: "text-rose-600",
        title: "text-rose-900",
        body: "text-rose-800",
      };
    case "pending":
      return {
        wrap: "border-sky-100 border-l-sky-500 bg-sky-50",
        icon: "text-sky-600",
        title: "text-sky-900",
        body: "text-sky-800",
      };
  }
}

/**
 * Convenience helper for the composer — formats the credit detail
 * string given the customer's limit + an optional outstanding total.
 * Outstanding will land with the receipts vertical; for now the
 * caller passes `null` and we render the limit alone.
 */
export function formatCreditDetail(
  limitPaise: bigint | null,
  outstandingPaise: bigint | null,
): string {
  if (limitPaise === null) return "No limit set";
  const limit = formatRupees(limitPaise);
  if (outstandingPaise === null) return `Limit ${limit} · usage lands with receipts`;
  const used = formatRupees(outstandingPaise);
  return `Used ${used} of ${limit}`;
}
